import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual } from 'lodash';
import moment from 'moment';
import DOMPurify from 'dompurify';

import HistoryLog from '../../../HistoryLog';
import HistoryField from '../../../HistoryField';
import {
  getAnticipateOtherHoursLabel,
  getAnticipateScheduleLabel,
  getInternalReportingRateLabel,
  getPreScreeningBilledToLabel,
  getDayTimeLabel,
  getTeammateFullNameLabel,
  getEmploymentCategoryLabel,
  getEmploymentTypeLabel,
  getWorkFromHomeLabel,
} from 'utils/assignmentUtils';
import {
  AnticipatedScheduleTypeEnum,
  NamedAttachmentModel,
} from 'api/backofficeApiClient';
import {
  AssignmentHistoryDetailsModel,
  AssignmentHistoryResponse,
  AssignmentTeammatesEditModel,
  AssignmentRateTypeEnum,
  AssignmentTeammateTypeEnum,
  TimePointEnum,
  TimeAMPMEnum,
} from 'api/adminApiClient';
import BaseTextEditorContent from 'components/wrappers/BaseTextEditor/BaseTextEditorContent';
import { getAssignmentSupportingDocumentFile } from 'redux/reducers/assignmentSlice';
import { downloadFile } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from '../../../HistoryCard.module.less';

type Props = {
  historyId: string;
  historyDetails: AssignmentHistoryDetailsModel;
};

export default function AssignmentInformationDetailsCard(props: Props) {
  const { historyId, historyDetails } = props;
  const { oldData, newData } = historyDetails;
  const dispatch = useAppDispatch();

  const hasJobTitleChanged = !isEqual(
    oldData?.assignmentInfo?.jobTitle || '',
    newData?.assignmentInfo?.jobTitle || '',
  );
  const hasJobDescriptionChanged = !isEqual(
    oldData?.assignmentInfo?.jobDescription || '',
    newData?.assignmentInfo?.jobDescription || '',
  );
  const hasWorkFromHomeChanged = !isEqual(
    oldData?.assignmentInfo?.workFromHome || '',
    newData?.assignmentInfo?.workFromHome || '',
  );
  const hasPreScreeningRequiredChanged = !isEqual(
    oldData?.assignmentInfo?.preScreeningRequired || '',
    newData?.assignmentInfo?.preScreeningRequired || '',
  );
  const hasPreScreeningBilledToChanged = !isEqual(
    oldData?.assignmentInfo?.preScreeningBilledTo || '',
    newData?.assignmentInfo?.preScreeningBilledTo || '',
  );
  const hasAssignmentTeammatesChanged = !isEqual(
    oldData?.assignmentTeammates || '',
    newData?.assignmentTeammates || '',
  );
  const hasJobCategoryChanged = !isEqual(
    oldData?.assignmentInfo?.jobCategory || '',
    newData?.assignmentInfo?.jobCategory || '',
  );
  const hasEmploymentTypeChanged = !isEqual(
    oldData?.assignmentInfo?.employmentType || '',
    newData?.assignmentInfo?.employmentType || '',
  );
  const hasCustomerPurchaseOrderChanged = !isEqual(
    oldData?.assignmentInfo?.customerPurchaseOrder || '',
    newData?.assignmentInfo?.customerPurchaseOrder || '',
  );
  const hasSupportingDocumentFilesChanged = !isEqual(
    (oldData?.assignmentInfo?.attachments || [])?.map(
      (attachment) => attachment.fileKey,
    ) || '',
    (newData?.assignmentInfo?.attachments || [])?.map(
      (attachment) => attachment.fileKey,
    ) || '',
  );
  const hasSpecialInstructionsChanged = !isEqual(
    oldData?.assignmentInfo?.specialInstructions || '',
    newData?.assignmentInfo?.specialInstructions || '',
  );
  const hasAnticipatedScheduleTypeChanged = !isEqual(
    oldData?.assignmentInfo?.anticipatedScheduleType || '',
    newData?.assignmentInfo?.anticipatedScheduleType || '',
  );
  const hasAnticipatedScheduleFromAMPMChanged = !isEqual(
    oldData?.assignmentInfo?.anticipatedSchedule?.fromAMPM || '',
    newData?.assignmentInfo?.anticipatedSchedule?.fromAMPM || '',
  );
  const hasAnticipatedScheduleToAMPMChanged = !isEqual(
    oldData?.assignmentInfo?.anticipatedSchedule?.toAMPM || '',
    newData?.assignmentInfo?.anticipatedSchedule?.toAMPM || '',
  );
  const hasAnticipatedScheduleToTimeChanged = !isEqual(
    oldData?.assignmentInfo?.anticipatedSchedule?.toTime || '',
    newData?.assignmentInfo?.anticipatedSchedule?.toTime || '',
  );
  const hasAnticipatedScheduleFromTimeChanged = !isEqual(
    oldData?.assignmentInfo?.anticipatedSchedule?.fromTime || '',
    newData?.assignmentInfo?.anticipatedSchedule?.fromTime || '',
  );
  const hasStartDateChanged = !isEqual(
    oldData?.assignmentInfo?.startDate || '',
    newData?.assignmentInfo?.startDate || '',
  );
  const hasEndDateChanged = !isEqual(
    oldData?.assignmentInfo?.endDate || '',
    newData?.assignmentInfo?.endDate || '',
  );

  const hasWorkCodeChanged = !isEqual(
    oldData?.assignmentInfo?.workCode || '',
    newData?.assignmentInfo?.workCode || '',
  );

  const hasAssignmentInformationChanged =
    hasJobTitleChanged ||
    hasJobDescriptionChanged ||
    hasWorkFromHomeChanged ||
    hasPreScreeningRequiredChanged ||
    hasPreScreeningBilledToChanged ||
    hasJobCategoryChanged ||
    hasEmploymentTypeChanged ||
    hasCustomerPurchaseOrderChanged ||
    hasSupportingDocumentFilesChanged ||
    hasSpecialInstructionsChanged ||
    hasAnticipatedScheduleTypeChanged ||
    hasAnticipatedScheduleFromAMPMChanged ||
    hasAnticipatedScheduleToTimeChanged ||
    hasAnticipatedScheduleFromTimeChanged ||
    hasAnticipatedScheduleToAMPMChanged ||
    hasStartDateChanged ||
    hasEndDateChanged ||
    hasWorkCodeChanged;

  const renderInternalReportingSection = (
    assignment?: AssignmentHistoryResponse,
  ) => {
    if (!assignment) return null;
    const sectionName = 'internal-reporting';

    const assignmentTeammates: AssignmentTeammatesEditModel[] = assignment
      .assignmentTeammates?.length
      ? assignment.assignmentTeammates
      : [
          {
            userId: '',
            rate: undefined,
            teammateType: AssignmentTeammateTypeEnum.Teammate,
            externalFullName: undefined,
            rateType: AssignmentRateTypeEnum.Percent,
          },
        ];
    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Internal reporting</div>
          </Col>

          <Col xs={24}>
            {hasAssignmentTeammatesChanged &&
              (assignmentTeammates || []).map(
                (teammate: AssignmentTeammatesEditModel, index: number) => (
                  <React.Fragment key={`${index}`}>
                    <Row gutter={[10, 10]}>
                      <HistoryField
                        title="Name"
                        value={getTeammateFullNameLabel(teammate)}
                        dataId={`${sectionName}-teammate-name-field`}
                      />

                      <HistoryField
                        title="Rate"
                        value={getInternalReportingRateLabel(teammate)}
                        dataId={`${sectionName}-rate-field`}
                      />
                    </Row>

                    <Divider />
                  </React.Fragment>
                ),
              )}
          </Col>
        </Row>

        <Divider />
      </>
    );
  };

  const handleSupportingFileDownload = async (file: NamedAttachmentModel) => {
    const result = await dispatch(
      getAssignmentSupportingDocumentFile({
        fileKey: file.fileKey || '',
        fileName: file.fileName || '',
        isAdmin: true,
      }),
    );

    if (getAssignmentSupportingDocumentFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const renderAssignmentInformationSection = (
    assignment?: AssignmentHistoryResponse,
  ) => {
    if (!assignment) return null;

    const { assignmentInfo } = assignment;
    const attachments = assignment.assignmentInfo?.attachments || [];

    const sectionName = 'assignment-information';
    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Assignment information</div>
          </Col>
          {hasJobTitleChanged && (
            <HistoryField
              title="Job title"
              value={assignmentInfo?.jobTitle}
              dataId={`${sectionName}-job-title-field`}
            />
          )}
          {hasJobDescriptionChanged && (
            <HistoryField
              title="Job description"
              value={
                assignmentInfo?.jobDescription ? (
                  <BaseTextEditorContent
                    content={DOMPurify.sanitize(assignmentInfo.jobDescription)}
                  />
                ) : undefined
              }
              dataId={`${sectionName}-job-description-field`}
            />
          )}
          {hasWorkFromHomeChanged && (
            <HistoryField
              title="Working location"
              value={getWorkFromHomeLabel(assignmentInfo?.workFromHome)}
              dataId={`${sectionName}-work-from-home-field`}
            />
          )}
          {hasPreScreeningRequiredChanged && (
            <HistoryField
              title="Pre-screen"
              value={
                assignmentInfo?.preScreeningRequired
                  ? 'Required'
                  : 'Not required'
              }
              dataId={`${sectionName}-pre-screen-field`}
            />
          )}
          {hasPreScreeningBilledToChanged && (
            <HistoryField
              title="Pre-screen billed to"
              value={getPreScreeningBilledToLabel(
                assignmentInfo?.preScreeningBilledTo,
              )}
              dataId={`${sectionName}-pre-screening-billed-to-field`}
            />
          )}
          {hasStartDateChanged && (
            <HistoryField
              title="Start date"
              value={moment.utc(assignmentInfo?.startDate).format('MM/DD/YYYY')}
              dataId={`${sectionName}-start-date-field`}
            />
          )}
          {hasEndDateChanged && (
            <HistoryField
              title="End date (expected)"
              value={moment.utc(assignmentInfo?.endDate).format('MM/DD/YYYY')}
              dataId={`${sectionName}-end-date-field`}
            />
          )}
          {hasJobCategoryChanged && (
            <HistoryField
              title="Job category"
              value={getEmploymentCategoryLabel(assignmentInfo?.jobCategory)}
              dataId={`${sectionName}-job-category-type-field`}
            />
          )}
          {hasEmploymentTypeChanged && (
            <HistoryField
              title="Employment type"
              value={getEmploymentTypeLabel(assignmentInfo?.employmentType)}
              dataId={`${sectionName}-employment-type-field`}
            />
          )}
          {hasCustomerPurchaseOrderChanged && (
            <HistoryField
              title="Customer PO Number"
              value={assignmentInfo?.customerPurchaseOrder}
              dataId={`${sectionName}-customer-purchase-order-field`}
            />
          )}
          {hasSupportingDocumentFilesChanged && (
            <HistoryField
              title="Supporting files"
              emptyText="No files"
              value={
                attachments.length > 0
                  ? attachments.map((file) => (
                      <div
                        className="link-imitation"
                        onClick={() => handleSupportingFileDownload(file)}
                      >
                        {file?.fileName}
                      </div>
                    ))
                  : null
              }
              dataId={`${sectionName}-supporting-documents-field`}
            />
          )}
          {hasAnticipatedScheduleTypeChanged && (
            <HistoryField
              title="Anticipated schedule"
              value={getAnticipateScheduleLabel(
                assignmentInfo?.anticipatedScheduleType,
              )}
              dataId={`${sectionName}-anticipated-schedule-type-field`}
            />
          )}
          {hasWorkCodeChanged && (
            <HistoryField
              title="WC Code"
              value={assignmentInfo?.workCode}
              dataId={`${sectionName}-wc-code-field`}
            />
          )}
          {assignmentInfo?.anticipatedScheduleType ===
            AnticipatedScheduleTypeEnum.Other && (
            <>
              {(hasAnticipatedScheduleFromAMPMChanged ||
                hasAnticipatedScheduleFromTimeChanged) && (
                <HistoryField
                  title="From"
                  value={
                    getAnticipateOtherHoursLabel(
                      assignmentInfo?.anticipatedSchedule
                        ?.fromTime as TimePointEnum,
                    ) +
                    ' ' +
                    getDayTimeLabel(
                      assignmentInfo?.anticipatedSchedule
                        ?.fromAMPM as TimeAMPMEnum,
                    )
                  }
                  dataId={`${sectionName}-from-time-field`}
                />
              )}
              {(hasAnticipatedScheduleToAMPMChanged ||
                hasAnticipatedScheduleToTimeChanged) && (
                <HistoryField
                  title="To"
                  value={
                    getAnticipateOtherHoursLabel(
                      assignmentInfo?.anticipatedSchedule
                        ?.toTime as TimePointEnum,
                    ) +
                    ' ' +
                    getDayTimeLabel(
                      assignmentInfo?.anticipatedSchedule
                        ?.toAMPM as TimeAMPMEnum,
                    )
                  }
                  dataId={`${sectionName}-to-time-field`}
                />
              )}
            </>
          )}
          {hasSpecialInstructionsChanged && (
            <HistoryField
              title="Special instructions"
              value={assignmentInfo?.specialInstructions}
              dataId={`${sectionName}-special-instructions-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderAssignmentInformation = (
    assignment?: AssignmentHistoryResponse,
  ) => {
    return (
      <>
        {hasAssignmentTeammatesChanged &&
          renderInternalReportingSection(assignment)}
        {hasAssignmentInformationChanged &&
          renderAssignmentInformationSection(assignment)}
      </>
    );
  };

  if (!hasAssignmentInformationChanged && !hasAssignmentTeammatesChanged)
    return null;

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderAssignmentInformation(oldData)}
      newData={renderAssignmentInformation(newData)}
    />
  );
}
